import { track } from "@amplitude/analytics-react-native";
import { ApolloError } from "@apollo/client";
import { useCallback, useState } from "react";
import { Platform } from "react-native";
import { usePathname, useUpdateSearchParams } from "solito/navigation";
import { amp } from "src/api/amplitude";
import JoinGatheringDocument from "src/api/graphql/__generated__/documents/JoinGatheringDocument";
import CollectDataField from "src/api/graphql/__generated__/enums/CollectDataField";
import GatheringAccessEnum from "src/api/graphql/__generated__/enums/GatheringAccessEnum";
import GatheringPrivacy from "src/api/graphql/__generated__/enums/GatheringPrivacy";
import UserType from "src/api/graphql/__generated__/enums/UserType";
import { useAsyncMutation } from "src/api/graphql/helper";
import native from "src/constants/native";
import hasValidGatheringAccess from "src/helpers/gatheringHelpers/hasValidGatheringAccess";
import log from "src/helpers/log";
import useCreateCallbackUrl from "src/hooks/useCreateCallbackUrl";
import useRouter from "src/hooks/useRouter";
import { useModal } from "src/modals";
import LoginModal from "src/modals/configs/LoginModal";
import useGlobalStore from "src/stores/useGlobalStore";
import { alert } from "src/swsh-native";
import toast from "src/swsh-native/toast";

const useJoinGathering = ({
	refCode,
	name,
	collectData,
}: {
	refCode: string;
	name: string | null | undefined;
	collectData: CollectDataField[] | null | undefined;
}) => {
	const mutateJoinGathering = useAsyncMutation(JoinGatheringDocument);
	const loggedIn = useGlobalStore((state) => state.loggedIn);
	const userType = useGlobalStore((state) => state.userType);
	const updateSearchParams = useUpdateSearchParams();
	const [loading, setLoading] = useState(false);
	const { presentModal } = useModal<LoginModal>();
	const router = useRouter();
	const pathname = usePathname();
	const createCallbackUrl = useCreateCallbackUrl();

	const joinGathering = useCallback(
		async ({
			password,
			noOperation = false,
		}: {
			password?: string;
			noOperation?: boolean;
		} = {}) => {
			try {
				if (!loggedIn || userType !== UserType.Full) {
					if (native) {
						let routeUrl = `/login?callback=${createCallbackUrl({
							addParams: {
								join: "true",
							},
						})}`;
						if (collectData && collectData.length > 0) {
							routeUrl += `&collectData=${collectData.join(",")}`;
						}
						router.push(routeUrl);
						return null;
					} else {
						router.replaceState(pathname ?? `/album/${refCode}`, {
							forwardParams: true,
							params: {
								join: "true",
							},
						});
						presentModal("LoginContainer", {
							props: {
								title: name ? `Join ${name}` : undefined,
								collectData: collectData ?? undefined,
							},
						});
						return null;
					}
				} else {
					setLoading(true);
					if (Platform.OS === "web") {
						updateSearchParams({ join: undefined });
					}
					const { data } = await mutateJoinGathering({
						input: {
							refCode,
							password,
							noOperation,
						},
						fetchPolicy: noOperation ? "no-cache" : "network-only",
					});
					if (
						data?.joinGathering?.access === GatheringAccessEnum.None &&
						!data?.joinGathering?.privacy?.includes(GatheringPrivacy.PasswordRequired)
					) {
						toast({
							preset: "error",
							title: "Unable to join album",
							message: "You do not have permission to join this album",
						});
					}
					if (hasValidGatheringAccess(data?.joinGathering)) {
						track(amp.gathering.joinGatheringSuccess, {
							gatheringId: data?.joinGathering?.gatheringId,
							_gathering: {
								gatheringId: data?.joinGathering?.gatheringId,
								numPhotos: data?.joinGathering?.numPhotos,
								numParticipants: data?.joinGathering?.numParticipants,
								numPhotosUploaded: data?.joinGathering?.numPhotosUploaded,
								ownerId: data?.joinGathering?.ownerId,
							},
						});
					}
					return data?.joinGathering;
				}
			} catch (err) {
				if (err instanceof ApolloError && err.message === "Album not yet published") {
					alert({
						title: "This album hasn't been published yet!",
						message: "Contact your host to ask them to publish this album.",
					});
				} else {
					log.error({ err }, "useJoinGathering");
					toast({
						preset: "error",
						title: "Error joining album",
						message: "Something went wrong",
					});
				}
				return null;
			} finally {
				setLoading(false);
			}
		},
		[
			collectData,
			createCallbackUrl,
			loggedIn,
			mutateJoinGathering,
			name,
			pathname,
			presentModal,
			refCode,
			router,
			updateSearchParams,
			userType,
		],
	);
	return {
		joinGathering,
		loading,
	};
};
export default useJoinGathering;
